import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import VUSeal from './VUSeal'
import { PROPERTIES, MODULES } from '../constants'
import { supabase } from '../supabaseClient'
import './LandingPage.css'

export default function LandingPage() {
  const navigate = useNavigate()
  const [form, setForm] = useState({ name: '', email: '', property: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const { data, error } = await supabase.auth.signUp({
        email: form.email,
        password: form.password,
        options: { data: { full_name: form.name, property: form.property } }
      })
      if (error) throw error
      if (data.user) {
        await supabase.from('profiles').upsert({
          id: data.user.id,
          full_name: form.name,
          property: form.property,
          role: 'gm',
        })
      }
      // Always show success state - never navigate away automatically
      setSuccess(true)
      setLoading(false)
    } catch (err) {
      setError(err.message)
      setLoading(false)
    }
  }

  return (
    <div className="lp">
      <nav className="lp-nav">
        <span className="lp-nav-logo">VACATIA UNIVERSITY</span>
        <button className="lp-nav-signin" onClick={() => navigate('/login')}>Sign in</button>
      </nav>

      <section className="lp-hero">
        <div className="lp-seal">
          <VUSeal size={340} tagline={true} />
        </div>
        <p className="lp-hero-eyebrow">Introducing</p>
        <h1 className="lp-hero-headline">The official training hub of Vacatia</h1>
        <div className="lp-hero-rule" />
        <p className="lp-hero-body">Built from your feedback. Delivered in community. Designed to make every property stronger.</p>
        <div className="lp-hero-actions">
          <a href="#signup" className="lp-btn-primary">Create your account</a>
          <a href="#curriculum" className="lp-btn-ghost">See the curriculum</a>
        </div>
      </section>

      <div className="lp-band">
        Now enrolling &nbsp;&mdash;&nbsp; <em>Curriculum 01: Branding</em> &nbsp;&mdash;&nbsp; Eight modules &nbsp;&mdash;&nbsp; Built around your immediate needs
      </div>

      <section className="lp-intro">
        <p className="lp-label">What is Vacatia University?</p>
        <h2 className="lp-heading">The official training hub of the Vacatia enterprise.</h2>
        <p className="lp-body">When we came together at Parkway in June, something real happened. Beyond the breakouts, we built camaraderie, a unified vision, and an honest picture of where every property stands. Those conversations became the foundation of Vacatia University. Curriculum 01 is Branding - because that is where you told us to start.</p>
        <div className="lp-stats">
          <div className="lp-stat"><span className="lp-stat-num">8</span><span className="lp-stat-lbl">Modules</span></div>
          <div className="lp-stat"><span className="lp-stat-num">90</span><span className="lp-stat-lbl">Min / session</span></div>
          <div className="lp-stat"><span className="lp-stat-num">15</span><span className="lp-stat-lbl">Properties</span></div>
          <div className="lp-stat"><span className="lp-stat-num">1</span><span className="lp-stat-lbl">Cohort</span></div>
        </div>
      </section>

      <section className="lp-curriculum" id="curriculum">
        <div className="lp-curriculum-head">
          <span className="lp-badge">Now open - Curriculum 01</span>
          <h2 className="lp-heading">Branding</h2>
          <p className="lp-body">Eight sessions. Every module delivers something real and builds on the last.</p>
        </div>
        <div className="lp-modules">
          {MODULES.map((mod) => (
            <div className="lp-module" key={mod.number}>
              <div className="lp-module-num">{mod.number}</div>
              <div className="lp-module-content">
                <h3 className="lp-module-title">{mod.title}</h3>
                <p className="lp-module-desc">{mod.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="lp-why">
        <p className="lp-label lp-label-light">Inside Vacatia University</p>
        <h2 className="lp-heading lp-heading-light">Tools built for your property. Available anytime.</h2>
        <div className="lp-why-grid">
          <div className="lp-why-card"><h3>Brand Bible & Guides</h3><p>The complete Vacatia brand standards always accessible in your resource library.</p></div>
          <div className="lp-why-card"><h3>Brand Voice Tool</h3><p>Type any situation and get back an on-brand response instantly.</p></div>
          <div className="lp-why-card"><h3>Session Recordings</h3><p>Every live session recorded and available in your module page.</p></div>
          <div className="lp-why-card"><h3>Direct Notes with Sam</h3><p>Leave a note after any module. Sam writes back inside the platform.</p></div>
        </div>
      </section>

      <section className="lp-signup" id="signup">
        <p className="lp-label">Get started</p>
        <h2 className="lp-heading">Create your account</h2>
        <p className="lp-signup-sub">Module 1 begins in August 2026. You are expected.</p>
        {success ? (
          <div className="lp-success">
            <h3>You're enrolled.</h3>
            <p>Your account has been created. Click below to sign in and access your dashboard.</p>
            <button className="lp-submit" style={{marginTop:'20px'}} onClick={() => navigate('/login')}>Sign in to your dashboard</button>
          </div>
        ) : (
          <form className="lp-form" onSubmit={handleSubmit}>
            <div className="lp-field"><label className="lp-lbl">Full name</label><input className="lp-input" type="text" name="name" placeholder="Your name" value={form.name} onChange={handleChange} required /></div>
            <div className="lp-field"><label className="lp-lbl">Email address</label><input className="lp-input" type="email" name="email" placeholder="name@vacatia.com" value={form.email} onChange={handleChange} required /></div>
            <div className="lp-field">
              <label className="lp-lbl">Your property</label>
              <select className="lp-select" name="property" value={form.property} onChange={handleChange} required>
                <option value="">Select your property</option>
                {PROPERTIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="lp-field"><label className="lp-lbl">Create a password</label><input className="lp-input" type="password" name="password" placeholder="At least 8 characters" value={form.password} onChange={handleChange} required minLength={8} /></div>
            {error && <p className="lp-error">{error}</p>}
            <button className="lp-submit" type="submit" disabled={loading}>{loading ? 'Creating account...' : 'Create account'}</button>
          </form>
        )}
      </section>

      <footer className="lp-footer">
        <p className="lp-footer-tagline">Leave better than you arrived.</p>
        <p className="lp-footer-copy">Vacatia University &nbsp;·&nbsp; Est. 2026 &nbsp;·&nbsp; The official training hub of Vacatia</p>
      </footer>
    </div>
  )
}